/**
 * 地図初期化ユーティリティ
 *
 * App.vue の init() から切り出した、Vueコンポーネントに依存しない
 * 地図セットアップ関連の関数群。
 */

import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import mlContour from 'maplibre-contour'
import { registerBlendModeProtocol } from '@/js/blendModeProtocol'
import { registerKotizuMaskProtocol } from '@/js/kotizuMaskProtocol'
import { registerSuperResProtocol } from '@/js/superResProtocol'

// ============================================================
// タッチスクロール処理
// ============================================================

/**
 * .scrollable-content や Vuetify の v-select に対応した
 * タッチスクロール処理を登録する
 */
export function setupTouchScrollHandlers(): void {
  let startY = 0
  let isTouching = false
  let currentTarget: HTMLElement | null = null
  let initialScrollTop = 0

  // v-selectのメニューが動的に追加された時にスクロール位置を調整
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        const menuContent = document.querySelector('.v-overlay__content') as HTMLElement | null
        if (menuContent) {
          observer.disconnect()
          menuContent.scrollTop = 200
        }
      }
    })
  })
  observer.observe(document.body, { childList: true, subtree: true })

  // タッチ開始時の処理
  document.addEventListener('touchstart', (e: TouchEvent) => {
    const target = (e.target as HTMLElement)?.closest('.scrollable-content, .v-overlay-container .v-select__content') as HTMLElement | null
    if (target) {
      startY = e.touches[0].clientY
      initialScrollTop = target.scrollTop
      isTouching = true
      currentTarget = target
      target.style.overflowY = 'auto'
    }
  }, { passive: false })

  // タッチ移動時の処理
  document.addEventListener('touchmove', (e: TouchEvent) => {
    if (!isTouching || !currentTarget) return
    const moveY = e.touches[0].clientY
    const deltaY = startY - moveY
    currentTarget.scrollTop += deltaY
    startY = moveY
    e.preventDefault()
  }, { passive: false })

  // タッチ終了時の処理
  document.addEventListener('touchend', () => {
    if (currentTarget) {
      currentTarget.style.overflowY = ''
    }
    currentTarget = null
    isTouching = false
    initialScrollTop = 0
  }, { passive: true })
}

// ============================================================
// MapLibreプロトコル登録
// ============================================================

/**
 * 画像タイルの特定色を透過にする共通処理
 */
function makeTransparent(
  imageData: ImageData,
  shouldTransparent: (r: number, g: number, b: number) => boolean
): void {
  const data = imageData.data
  for (let i = 0; i < data.length; i += 4) {
    if (shouldTransparent(data[i], data[i + 1], data[i + 2])) {
      data[i + 3] = 0
    }
  }
}

/**
 * URLからタイルを取得し、指定色を透過にして返すプロトコルハンドラを生成
 */
function createTransparentProtocolHandler(
  protocolPrefix: string,
  shouldTransparent: (r: number, g: number, b: number) => boolean
) {
  return (params: { url: string }) => {
    return new Promise<{ data: ArrayBuffer; contentType: string }>((resolve, reject) => {
      try {
        const url = params.url.replace(`${protocolPrefix}://`, '')
        fetch(url)
          .then(response => {
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
            return response.blob()
          })
          .then(blob => createImageBitmap(blob))
          .then(image => {
            const canvas = document.createElement('canvas')
            canvas.width = image.width
            canvas.height = image.height
            const ctx = canvas.getContext('2d')!
            ctx.drawImage(image, 0, 0)
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            makeTransparent(imgData, shouldTransparent)
            ctx.putImageData(imgData, 0, 0)
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Blob creation failed'))
                return
              }
              const reader = new FileReader()
              reader.onload = function () {
                resolve({ data: reader.result as ArrayBuffer, contentType: 'image/png' })
              }
              reader.onerror = function () {
                reject(new Error('FileReader error while reading blob'))
              }
              reader.readAsArrayBuffer(blob)
            }, 'image/png')
          })
          .catch(error => reject(new Error(`Processing error: ${error.message}`)))
      } catch (error: unknown) {
        reject(new Error(`Unexpected error: ${(error as Error).message}`))
      }
    })
  }
}

/**
 * MapLibre GL JS のカスタムプロトコルを一括登録する
 * - PMTiles
 * - 等高線（maplibre-contour）
 * - ブレンドモード
 * - transparentPmtiles / transparentBlack / transparentWhite
 */
export function setupMapProtocols(): void {
  // ------ PMTiles プロトコル ------
  const protocol = new Protocol()
  const pmtilesProtocol = new Protocol()
  maplibregl.addProtocol('pmtiles', protocol.tile)

  // ------ 等高線（maplibre-contour）プロトコル ------
  const contourDemSource = new mlContour.DemSource({
    url: 'https://kenzkenz.net/myphp/demTerrarium.php?z={z}&y={y}&x={x}',
    encoding: 'terrarium',
    maxzoom: 15,
    worker: true
  })
  contourDemSource.setupMaplibre(maplibregl)
  // グローバルにアクセス可能にする（レイヤー追加時に使用）
  ;(window as unknown as Record<string, unknown>).contourDemSource = contourDemSource

  // ------ ブレンドモードプロトコル ------
  registerBlendModeProtocol(maplibregl)

  // ------ 大正古地図マスキングプロトコル ------
  registerKotizuMaskProtocol(maplibregl)

  // ------ 航空写真・地図 超解像プロトコル ------
  registerSuperResProtocol(maplibregl)

  // ------ transparentPmtiles プロトコル ------
  maplibregl.addProtocol('transparentPmtiles', (params: { url: string; tile?: { z: number; x: number; y: number } }) => {
    return new Promise((resolve, reject) => {
      try {
        if (!params.url.startsWith('transparentPmtiles://')) {
          reject(new Error('Invalid protocol: URL must start with transparentPmtiles://'))
          return
        }
        let pmtilesUrl = params.url.replace('transparentPmtiles://', 'pmtiles://')
        const tileMatch = params.url.match(/\/(\d+)\/(\d+)\/(\d+)$/)
        let tileParams: { z: number; x: number; y: number }
        if (tileMatch) {
          tileParams = { z: parseInt(tileMatch[1], 10), x: parseInt(tileMatch[2], 10), y: parseInt(tileMatch[3], 10) }
        } else if (params.tile && typeof params.tile.z === 'number') {
          tileParams = params.tile
        } else {
          reject(new Error('No valid tile coordinates found in URL or params'))
          return
        }
        pmtilesUrl = pmtilesUrl.replace(/\/\d+\/\d+\/\d+$/, '/{z}/{x}/{y}')
        const urlMatch = pmtilesUrl.match(/^pmtiles:\/\/(.+)\/\{z\}\/\{x\}\/\{y\}$/)
        if (!urlMatch) {
          reject(new Error('Invalid PMTiles URL format'))
          return
        }
        ;(pmtilesProtocol as unknown as { tile: (p: unknown, cb: (err: unknown, data?: unknown) => void) => void }).tile(
          { ...params, url: pmtilesUrl, tile: tileParams },
          (err: unknown, data?: unknown) => {
            if (err) {
              reject(new Error(`PMTiles error: ${err}`))
              return
            }
            if (!data) {
              reject(new Error('No data returned from PMTiles'))
              return
            }
            const blob = new Blob([data as ArrayBuffer], { type: 'image/png' })
            createImageBitmap(blob)
              .then((image) => {
                const canvas = document.createElement('canvas')
                canvas.width = image.width
                canvas.height = image.height
                const ctx = canvas.getContext('2d')!
                ctx.drawImage(image, 0, 0)
                const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                makeTransparent(imgData, (r, g, b) =>
                  (r === 0 && g === 0 && b === 0) ||
                  (r === 0 && g === 0 && b >= 254) ||
                  (r === 255 && g === 255 && b === 255)
                )
                ctx.putImageData(imgData, 0, 0)
                canvas.toBlob((blob) => {
                  if (!blob) { reject(new Error('Blob creation failed')); return }
                  const reader = new FileReader()
                  reader.onload = () => resolve({ data: reader.result, contentType: 'image/png' } as never)
                  reader.onerror = () => reject(new Error('FileReader error'))
                  reader.readAsArrayBuffer(blob)
                }, 'image/png')
              })
              .catch((error) => reject(new Error(`Image processing error: ${error.message}`)))
          }
        )
      } catch (error: unknown) {
        reject(new Error(`Unexpected error: ${(error as Error).message}`))
      }
    })
  })

  // ------ transparentBlack プロトコル ------
  maplibregl.addProtocol('transparentBlack', createTransparentProtocolHandler(
    'transparentBlack',
    (r, g, b) =>
      (r === 0 && g === 0 && b === 0) ||
      (r === 0 && g === 0 && b >= 254) ||
      (r === 255 && g === 255 && b === 255)
  ))

  // ------ transparentWhite プロトコル ------
  maplibregl.addProtocol('transparentWhite', createTransparentProtocolHandler(
    'transparentWhite',
    (r, g, b) => r === 255 && g === 255 && b === 255
  ))
}

// ============================================================
// 地図インスタンス生成
// ============================================================

interface MapInitParams {
  lng?: number
  lat?: number
  zoom?: number
  bearing?: number
  pitch01?: number
  pitch02?: number
  split?: string
}

interface MapSizeConfig {
  width: string
  height: string
  top?: string
}

/**
 * MapLibre GL JS の地図インスタンスを生成する
 * map01〜map04のループ処理
 */
export function createMapInstances(
  vm: {
    mapNames: string[]
    mapSpeedTuners: Record<string, (() => void) | null>
    s_map2Flg: boolean
    mapSize: Record<string, MapSizeConfig>
    getMapSpeedOverrides: (profile: string) => unknown
    $store: { state: Record<string, unknown> }
  },
  params: MapInitParams,
  useGsiTerrainSource: (addProtocol: typeof maplibregl.addProtocol, opts: unknown) => unknown,
  tuneMapInteractionSpeed: (map: unknown, overrides: unknown) => () => void,
  startMapViewLogging: (map: unknown, opts: unknown) => void,
): void {
  vm.mapNames.forEach(mapName => {
    // 既存のスピードチューナーをクリーンアップ
    if (vm.mapSpeedTuners?.[mapName]) {
      try { vm.mapSpeedTuners[mapName]!() } catch (_) { /* */ }
      vm.mapSpeedTuners[mapName] = null
    }

    // 2画面モード設定
    if (mapName === 'map02') {
      if (params.split === 'true') {
        vm.s_map2Flg = true
        if (window.innerWidth > 1000) {
          vm.mapSize.map01.width = '50%'
          vm.mapSize.map01.height = '100%'
        } else {
          vm.mapSize.map01.width = '100%'
          vm.mapSize.map01.height = '50%'
          vm.mapSize.map02.width = '100%'
          vm.mapSize.map02.height = '50%'
          vm.mapSize.map02.top = '50%'
        }
      }
    }

    // 中心座標とズーム
    let center: [number, number] = [139.84267451046338, 38.36453863570733]
    let zoom = 5
    let bearing = 0
    if (params.lng && !isNaN(params.lng) && !isNaN(params.lat!) && !isNaN(params.zoom!)) {
      center = [params.lng, params.lat!]
      zoom = params.zoom!
      bearing = params.bearing || 0
    }

    // 地形ソース
    const gsiTerrainSource = useGsiTerrainSource(maplibregl.addProtocol, {
      tileUrl: 'https://tiles.gsj.jp/tiles/elev/mixed/{z}/{y}/{x}.png',
      maxzoom: 17,
      attribution: '<a href="https://gbank.gsj.jp/seamless/elev/">産総研シームレス標高タイル</a>',
    })

    // ピッチ
    let pitch = mapName === 'map01' ? (params.pitch01 || 0) : (params.pitch02 || 0)
    pitch = isNaN(pitch) ? 0 : pitch

    // iOS判定
    const isIOS = vm.$store.state.isIOS as boolean

    // 地図インスタンス生成
    // @ts-ignore: MapLibreの型定義にない実験的オプション（prefetchZoomDelta等）を使用
    const map = new maplibregl.Map({
      container: mapName,
      localIdeographFontFamily: 'sans-serif',
      center,
      zoom,
      maxZoom: 24.4,
      pitch,
      bearing,
      maxPitch: 85,
      attributionControl: false,
      fadeDuration: 0,
      renderWorldCopies: true,
      maxTileCacheZoomLevels: isIOS ? 1 : 5,
      maxTileCacheSize: isIOS ? 15 : 50,
      prefetchZoomDelta: isIOS ? 0 : 1,
      experimentalZoomLevelsToOverscale: 4,
      canvasContextAttributes: {
        contextType: isIOS ? 'webgl' : 'webgl2',
        antialias: false,
        preserveDrawingBuffer: true,
      },
      antialias: false,
      workerCount: 1,
      style: {
        version: 8,
        glyphs: 'https://tile.openstreetmap.jp/fonts/{fontstack}/{range}.pbf',
        sprite: 'https://maputnik.github.io/osm-liberty/sprites/osm-liberty',
        sources: {
          terrain: gsiTerrainSource,
        },
        layers: [],
      },
    } as never)

    // スピードチューニング
    const initialOverrides = vm.getMapSpeedOverrides(vm.$store.state.mapSpeedProfile as string)
    const detachSpeedTuning = tuneMapInteractionSpeed(map, initialOverrides)
    vm.mapSpeedTuners[mapName] = detachSpeedTuning

    // storeに格納
    vm.$store.state[mapName] = map

    // HMRエラーハンドラ
    map.on('error', (e: { error?: Error }) => {
      if (e.error?.message?.includes('placement') || e.error?.message?.includes('rgb') || e.error?.message?.includes('proxy')) {
        console.warn('[MapLibre] Error suppressed (HMR):', e.error.message)
        return
      }
      console.error('[MapLibre] Map error:', e)
    })

    map.once('load', () => {
      // z-index check (debug removed)
    })

    // 地図閲覧ログ
    if (mapName === 'map01') {
      startMapViewLogging(map, { interval: 30000, enabled: true })
    }
  })
}
